import { TextAlignment } from "common/utils/textAlignment";

const DXF_ALIGNMENT_TYPES = {
  pxql: TextAlignment.LEFT,
  pxqc: TextAlignment.CENTER,
  pxqr: TextAlignment.RIGHT,
  pxqj: TextAlignment.CENTER, // justify
};

const DXF_RTF_TEXT_OVERRIDE_CODES = {
  underline: /\\L/, // Underline
  overline: /\\O/, // Overline
  strikeThrough: /\\K/, // Strikethrough
  color: /\\C\d+;?/, // Color override (e.g., \C1, \C5;)
  height: /\\H\d+(\.\d+)?;?/, // Height override (e.g., \H2.5;)
  widthFactor: /\\W\d+(\.\d+)?;?/, // Width factor (e.g., \W0.8;)
  obliqueAngle: /\\Q-?\d+(\.\d+)?;?/, // Obliquing angle (e.g., \Q30;)
  font: /\\[Ff][^;]+;/, // Font override (e.g., \FArial; or \fISOCPEUR|b0|i0|c0|p34;)
  stacking: /\\S[^;]+;/, // Stacked text (e.g., \S1/2;)
  alignment: /\\A[0-2];?/, // Vertical alignment (0=bottom, 1=middle, 2=top)
  tracking: /\\T\d+(\.\d+)?;?/, // Character spacing (e.g., \T2;)
  bgColor: /\\K\d+;?/, // Background color
  rotation: /\\R-?\d+(\.\d+)?;?/, // Rotation (e.g., \R90;)
  paragraph: /\\P/, // Paragraph break
  degree: /\\D/, // Degree symbol (°)
  plus_minus: /\\p/, // Plus/minus (±)
  unicode: /\\U\+[0-9A-F]{4}/, // Unicode character
  paragraphAlignment: /\\(pxql|pxqc|pxqr|pxqj);/, // Paragraph alignment
};

const REMOVE_BRACES_REGEX = /[{}]/g;
const REMOVE_SEMICOLONS_REGEX = /[;]/g;

function applyDxfTextSpecials(text: string): string {
  return text
    .replace(/%%[Cc]/g, "Ø")
    .replace(/%%[Dd]/g, "°")
    .replace(/%%[Pp]/g, "±");
}

export function parseDXFTextRTFOverrides(text: string) {
  let parsedText = text;
  let parsedTextAlignment: TextAlignment | undefined = undefined;

  // we only do this once, even though there may be multiple.
  // this parser only supports alignment using the first instance
  const topLevelAlignment = parsedText.match(DXF_RTF_TEXT_OVERRIDE_CODES.paragraphAlignment);
  if (topLevelAlignment) {
    const [match, alignmentOption] = topLevelAlignment;
    parsedText = parsedText.replace(match, "");
    parsedTextAlignment = DXF_ALIGNMENT_TYPES[alignmentOption];
  }

  const paragraphs = parsedText.split(DXF_RTF_TEXT_OVERRIDE_CODES.paragraph);
  const parsedParagraphs = paragraphs.map((paragraph) => {
    // strip out all the text override codes.
    // this is written this way so that we can eventially include some of them
    // in the text styling if we need to
    for (const value of Object.values(DXF_RTF_TEXT_OVERRIDE_CODES)) {
      // the top level flags look for a single instance of the code, but we want to remove all of them
      paragraph = paragraph.replace(new RegExp(value.source, "g"), "");
    }

    paragraph = paragraph.replace(REMOVE_BRACES_REGEX, "");
    paragraph = paragraph.replace(REMOVE_SEMICOLONS_REGEX, "");
    return applyDxfTextSpecials(paragraph);
  });

  parsedText = parsedParagraphs.join("\n");

  return {
    text: parsedText,
    alignment: parsedTextAlignment,
  };
}

const DXF_TEXT_ESCAPE_CODES = {
  underline: /%%U/, // Underline
  center: /%%C/, // Center text
  left: /%%L/, // Left align
  right: /%%R/, // Right align
  degree: /%%P/, // Degree symbol (°)
  percent: /%%/, // Percent symbol
  scaling: /%%S/, // Scale (usually related to dimension scaling)
};

export function parseDXFTextOverrides(text: string) {
  let parsedText = text;
  let parsedTextAlignment: TextAlignment | undefined = undefined;

  // Handle escape sequences for text formatting
  // This will search for common text formatting commands like %%U, %%C, etc.
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.underline, ""); // Handle Underline
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.center, ""); // Handle Center
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.left, ""); // Handle Left Align
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.right, ""); // Handle Right Align
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.degree, "°"); // Replace degree symbol
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.percent, "%"); // Replace percent symbol
  parsedText = parsedText.replace(DXF_TEXT_ESCAPE_CODES.scaling, ""); // Handle scaling

  // Check if the alignment flags are present
  const alignmentMatch = text.match(/%%(C|L|R)/); // This checks for %%C, %%L, %%R for alignment
  if (alignmentMatch) {
    const alignmentFlag = alignmentMatch[0];
    parsedTextAlignment = DXF_ALIGNMENT_TYPES[alignmentFlag.slice(2).toLowerCase()];
  }

  parsedText = parsedText.replace(REMOVE_BRACES_REGEX, "");
  parsedText = parsedText.replace(REMOVE_SEMICOLONS_REGEX, "");

  return {
    text: parsedText,
    alignment: parsedTextAlignment,
  };
}
